/**
 * Sentry Frontend Initialization
 * 
 * Initializes Sentry for React error tracking, performance monitoring,
 * and session replay.
 */

import * as Sentry from "@sentry/react";

// Only initialize Sentry if DSN is configured
const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;

if (SENTRY_DSN) {
  Sentry.init({
    dsn: SENTRY_DSN,
    
    // Environment configuration
    environment: import.meta.env.MODE || 'development',
    
    // Release tracking
    release: import.meta.env.VITE_SENTRY_RELEASE || undefined,
    
    // Integrations
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        // Mask all text and input content for privacy
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
    
    // Performance Monitoring
    tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0, // 10% in prod, 100% in dev
    
    // Trace propagation to backend
    tracePropagationTargets: [
      "localhost",
      /^https:\/\/.*\.replit\.dev/,
      /^https:\/\/techalign\.app/,
    ],
    
    // Session Replay
    replaysSessionSampleRate: import.meta.env.PROD ? 0.1 : 1.0, // 10% in prod
    replaysOnErrorSampleRate: 1.0, // Always capture replays when errors occur
    
    // Enable logs
    enableLogs: true,
    
    // PII Configuration
    sendDefaultPii: false, // Don't send PII by default
    
    // Filter out sensitive data
    beforeSend(event, hint) {
      // Remove sensitive data from breadcrumbs
      if (event.breadcrumbs) {
        event.breadcrumbs = event.breadcrumbs.map(breadcrumb => {
          if (breadcrumb.data) {
            // Remove sensitive fields
            const sensitiveFields = ['password', 'token', 'secret', 'api_key', 'authorization'];
            sensitiveFields.forEach(field => {
              if (breadcrumb.data && field in breadcrumb.data) {
                breadcrumb.data[field] = '[FILTERED]';
              }
            });
          }
          return breadcrumb;
        });
      }
      
      return event;
    },
    
    // Ignore certain errors
    ignoreErrors: [
      // Browser extensions
      'top.GLOBALS',
      'originalCreateNotification',
      'canvas.contentDocument',
      'MyApp_RemoveAllHighlights',
      'fb_xd_fragment',
      'bmi_SafeAddOnload',
      'EBCallBackMessageReceived',
      'conduitPage',
      // Network errors that aren't actionable
      'NetworkError',
      'Failed to fetch',
      'Load failed',
      // React Query errors (already handled)
      'Query data cannot be undefined',
    ],
  });

  // Sentry initialized successfully
} else {
  // Sentry DSN not configured - error tracking disabled
}

export { Sentry };
